import React, { useState, useEffect, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Switch,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardMedia,
  Rating,
  Chip,
  Grid,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Language as LanguageIcon,
  DarkMode as DarkModeIcon,
  Edit as EditIcon,
  Favorite as FavoriteIcon,
  Star as StarIcon,
  Comment as CommentIcon,
  ThumbUp as ThumbUpIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../store';
import { logout, fetchUser } from '../store/slices/authSlice';
import AuthForm from '../components/AuthForm';
import { Restaurant, User, Review, Vote } from '../types';
import { API_BASE_URL } from '../config';

const AccountPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { token, user, loading } = useAppSelector((state) => state.auth);
  const [darkMode, setDarkMode] = useState(false);
  const [notifications, setNotifications] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    console.log('AccountPage - Current auth state:', { token, user, loading });
    if (!token) {
      console.log('AccountPage - No token found, redirecting to login');
      navigate('/login');
    } else if (!user && !loading) {
      console.log('AccountPage - Token found but no user data, fetching user...');
      dispatch(fetchUser());
    }
  }, [token, user, loading, navigate, dispatch]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const renderFavorites = () => (
    <Grid container spacing={3}>
      {(user?.favorites || []).map((restaurant: Restaurant) => (
        <Grid item xs={12} sm={6} md={4} key={restaurant.id}>
          <Card>
            <CardMedia
              component="img"
              height="140"
              image={restaurant.image_url || restaurant.images?.[0]?.url || '/placeholder.jpg'}
              alt={restaurant.name}
            />
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {restaurant.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {restaurant.description}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Rating value={restaurant.rating || 0} readOnly size="small" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderReviews = () => (
    <Grid container spacing={3}>
      {(user?.reviews || []).map((review: Review) => (
        <Grid item xs={12} key={review.id}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {review.restaurant?.name}
              </Typography>
              <Rating value={review.rating} readOnly />
              <Typography variant="body1" sx={{ mt: 1 }}>
                {review.comment}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(review.created_at).toLocaleDateString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderVotes = () => (
    <Grid container spacing={3}>
      {(user?.votes || []).map((vote: Vote) => (
        <Grid item xs={12} key={vote.id}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {vote.restaurant?.name}
              </Typography>
              <Chip
                icon={vote.vote_type === 'up' ? <ThumbUpIcon /> : <ThumbUpIcon />}
                label={vote.vote_type === 'up' ? 'Upvoted' : 'Downvoted'}
                color={vote.vote_type === 'up' ? 'primary' : 'error'}
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {new Date(vote.created_at).toLocaleDateString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  if (!token) {
    return (
      <Container maxWidth="lg" sx={{ py: 3, pb: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Welcome to Atlas.Rest
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please login or register to access your account
          </Typography>
        </Box>
        <AuthForm onSuccess={() => navigate('/account')} />
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 3, pb: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ py: 3, pb: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Error Loading Account
          </Typography>
          <Typography variant="body1" color="error">
            Unable to load user data. Please try logging in again.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/login')}
            sx={{ mt: 2 }}
          >
            Back to Login
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3, pb: 8 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4">
          Account Settings
        </Typography>
        <Button
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
        >
          Logout
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{ width: 100, height: 100, mb: 2 }}
                >
                  {user.first_name?.[0]}{user.last_name?.[0]}
                </Avatar>
                <Typography variant="h6">
                  {user.first_name} {user.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {user.email}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <List>
                <ListItem>
                  <ListItemIcon>
                    <NotificationsIcon />
                  </ListItemIcon>
                  <ListItemText primary="Notifications" />
                  <Switch
                    edge="end"
                    checked={notifications}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setNotifications(e.target.checked)}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <DarkModeIcon />
                  </ListItemIcon>
                  <ListItemText primary="Dark Mode" />
                  <Switch
                    edge="end"
                    checked={darkMode}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setDarkMode(e.target.checked)}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab icon={<FavoriteIcon />} label="Favorites" />
                <Tab icon={<StarIcon />} label="Reviews" />
                <Tab icon={<ThumbUpIcon />} label="Votes" />
              </Tabs>
              <Box sx={{ mt: 2 }}>
                {activeTab === 0 && renderFavorites()}
                {activeTab === 1 && renderReviews()}
                {activeTab === 2 && renderVotes()}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AccountPage; 